import { IconDeviceFloppy, IconPlus, IconTrash } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Card, IconButton, Input, Select, Text, Textarea } from "@/components/ui";
import { qk } from "@/lib/query-keys";
import { toastErrorApi, toastExito } from "@/lib/toast";
import {
  guardarColumnaPersonalizada,
  type Catalogo,
  type RelacionSugerida,
  type RepDefinicion,
} from "../services/reporteria.api";
import { useColumnasPersonalizadas } from "../hooks/use-reporteria";
import * as ops from "../state/builder-ops";
import { JoinCanvas } from "./JoinCanvas";

export function RelacionesYFormulas({
  def,
  setDef,
  catalogo,
  relaciones,
}: {
  def: RepDefinicion;
  setDef: (f: (d: RepDefinicion) => RepDefinicion) => void;
  catalogo: Catalogo;
  relaciones: RelacionSugerida[];
}) {
  const qc = useQueryClient();
  const columnasGuardadas = useColumnasPersonalizadas();

  const guardarCol = useMutation({
    mutationFn: guardarColumnaPersonalizada,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.repColumnas });
      toastExito({ title: "Columna guardada", message: "Disponible para reutilizar en otros reportes." });
    },
    onError: toastErrorApi,
  });

  const etiquetas = def.columnas.map((c) => c.etiqueta ?? c.token);

  return (
    <Card className="space-y-4 p-3">
      {/* Diseñador visual de relaciones · siempre visible (también es la zona
          donde se pueden soltar tablas arrastradas desde "Datos disponibles"). */}
      <JoinCanvas def={def} setDef={setDef} catalogo={catalogo} relaciones={relaciones} />

      {/* Columnas calculadas */}
      <div>
        <div className="mb-1.5 flex items-center justify-between">
          <Text fw={600} size="sm">
            Columnas calculadas
          </Text>
          {columnasGuardadas.data && columnasGuardadas.data.length > 0 && (
            <div className="w-52">
              <Select
                size="sm"
                placeholder="Usar columna guardada…"
                value=""
                onValueChange={(v) => {
                  const guardada = columnasGuardadas.data!.find((c) => String(c.id) === v);
                  if (guardada)
                    setDef((d) =>
                      ops.addCalc(d, {
                        nombre: guardada.nombre,
                        formula: guardada.formula,
                        tipo: guardada.tipo,
                        formato: guardada.formato,
                      })
                    );
                }}
                options={columnasGuardadas.data.map((c) => ({ value: String(c.id), label: c.nombre }))}
              />
            </div>
          )}
        </div>

        {etiquetas.length > 0 && (
          <Text size="xs" c="dimmed" className="mb-1.5">
            Referencia columnas por su etiqueta entre corchetes:{" "}
            {etiquetas.slice(0, 8).map((e) => (
              <Badge key={e} size="xs" variant="light" className="mr-1">
                [{e}]
              </Badge>
            ))}
          </Text>
        )}

        <div className="space-y-2">
          {(def.columnasCalculadas ?? []).map((c, i) => (
            <div key={i} className="space-y-1 rounded-md border border-border p-2">
              <div className="flex items-center gap-1.5">
                <Input
                  className="w-40"
                  placeholder="Nombre"
                  value={c.nombre}
                  onChange={(e) => setDef((d) => ops.updateCalc(d, i, { nombre: e.currentTarget.value }))}
                />
                <div className="w-36">
                  <Select
                    size="sm"
                    value={c.tipo}
                    onValueChange={(v) => setDef((d) => ops.updateCalc(d, i, { tipo: v }))}
                    options={[
                      { value: "fila", label: "Por fila" },
                      { value: "agregado", label: "Agregado (total)" },
                    ]}
                  />
                </div>
                <IconButton
                  aria-label="Guardar columna"
                  variant="subtle"
                  size="sm"
                  disabled={!c.nombre.trim() || !c.formula.trim()}
                  onClick={() =>
                    guardarCol.mutate({ nombre: c.nombre, formula: c.formula, tipo: c.tipo, formato: c.formato })
                  }
                >
                  <IconDeviceFloppy size={16} />
                </IconButton>
                <IconButton aria-label="Quitar" variant="subtle" size="sm" onClick={() => setDef((d) => ops.removeCalc(d, i))}>
                  <IconTrash size={16} />
                </IconButton>
              </div>
              <Textarea
                rows={2}
                placeholder="Fórmula NCalc · ej. [Subtotal] * 1.16"
                value={c.formula}
                onChange={(e) => setDef((d) => ops.updateCalc(d, i, { formula: e.currentTarget.value }))}
              />
            </div>
          ))}
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={() => setDef((d) => ops.addCalc(d, { nombre: "", formula: "", tipo: "fila", formato: null }))}
          >
            Agregar columna calculada
          </Button>
        </div>
      </div>

      {/* Filtro por fórmula */}
      <div>
        <Text fw={600} size="sm" className="mb-1.5">
          Filtro por fórmula
        </Text>
        <Textarea
          rows={2}
          placeholder="Expresión booleana · ej. [Total] > 1000 and [Estatus] = 'Vigente'"
          value={def.filtroFormula ?? ""}
          onChange={(e) => setDef((d) => ops.setFiltroFormula(d, e.currentTarget.value))}
        />
        <Text size="xs" c="dimmed" className="mt-1">
          Se evalúa en memoria sobre el resultado (afecta el rendimiento en reportes grandes).
        </Text>
      </div>
    </Card>
  );
}
